/**
 * Server-managed fields can cause backend validation/ownership conflicts, so we strip them before sending payloads.
 */
export function sanitizePayload<T extends Record<PropertyKey, unknown>>(
    data: T,
    options?: { ignoreFields?: readonly (keyof T)[]; skipIdFields?: boolean }
): Partial<T> {
    const ID_FIELDS = options?.skipIdFields ? [] : (['documentId', 'id'] as const)
    const SERVER_FIELDS = [
        'createdAt',
        'publishedAt',
        'updatedAt',
        'createdBy',
        'updatedBy',
        'localizations',
        'locale',
    ] as const
    const ignoreFields = options?.ignoreFields ?? []

    const ignore = new Set<PropertyKey>([
        ...ID_FIELDS,
        ...SERVER_FIELDS,
        ...ignoreFields,
    ] as PropertyKey[])

    const out: Partial<T> = {}
    for (const key of Object.keys(data) as Array<keyof T>) {
        if (!ignore.has(key)) out[key] = data[key]
    }
    return out
}

/**
 * Serializes Date objects to ISO string format for API compatibility.
 * Ensures validation and API calls use consistent string format.
 * Recursively processes nested objects and arrays.
 *
 * @param obj - The object to serialize
 * @returns A new object with Date objects converted to ISO strings
 */
export function serializeForValidation<T>(obj: T): T {
    if (obj instanceof Date) {
        return obj.toISOString() as unknown as T
    }

    if (Array.isArray(obj)) {
        return obj.map(serializeForValidation) as unknown as T
    }

    if (typeof obj === 'object' && obj !== null) {
        const result: Record<string, unknown> = {}
        for (const key in obj) {
            result[key] = serializeForValidation(obj[key])
        }
        return result as T
    }

    return obj
}

/**
 * Removes undefined, null, and empty string fields from an object.
 * This prevents sending empty fields to the server which might trigger validation errors.
 *
 * @param obj - The object to clean
 * @returns A new object with empty fields removed
 */
export function removeEmptyFields<T extends Record<string, unknown>>(obj: T): Partial<T> {
    const result: Partial<T> = {}

    for (const key in obj) {
        const value = obj[key]

        // Skip undefined, null, and empty strings
        if (value === undefined || value === null || value === '') {
            continue
        }

        // Recursively clean nested objects
        if (typeof value === 'object' && !Array.isArray(value) && value !== null) {
            const cleaned = removeEmptyFields(value as Record<string, unknown>)
            if (Object.keys(cleaned).length > 0) {
                result[key] = cleaned as T[Extract<keyof T, string>]
            }
        } else {
            result[key] = value
        }
    }

    return result
}

/**
 * Removes empty strings from an object while preserving null values.
 * This is needed for updates where:
 * - Empty strings would cause backend validation errors
 * - Null values need to be preserved for field dependencies
 * - Undefined values should be excluded from the payload
 *
 * @param obj - The object to clean
 * @returns A new object with empty strings and undefined values removed
 */
export function removeEmptyStrings<T extends Record<string, unknown>>(obj: T): Partial<T> {
    const result: Partial<T> = {}

    for (const key in obj) {
        const value = obj[key]

        // Skip empty strings and undefined, but keep null
        if (value === '' || value === undefined) {
            continue
        }

        // Recursively clean nested objects (but not arrays)
        if (typeof value === 'object' && !Array.isArray(value) && value !== null) {
            const cleaned = removeEmptyStrings(value as Record<string, unknown>)
            if (Object.keys(cleaned).length > 0) {
                result[key] = cleaned as T[Extract<keyof T, string>]
            }
        } else {
            result[key] = value
        }
    }

    return result
}

/**
 * Converts empty strings to null while preserving other values.
 * This is needed for updates where we want to explicitly clear fields in Strapi.
 * - Empty strings → null (tells Strapi to clear the field)
 * - Undefined → removed (field not in payload)
 * - Other values → preserved as-is
 *
 * @param obj - The object to clean
 * @returns A new object with empty strings converted to null
 */
export function convertEmptyStringsToNull<T extends Record<string, unknown>>(obj: T): Partial<T> {
    const result: Partial<T> = {}

    for (const key in obj) {
        const value = obj[key]

        // Skip undefined (exclude from payload)
        if (value === undefined) {
            continue
        }

        // Convert empty strings to null
        if (value === '') {
            result[key] = null as T[Extract<keyof T, string>]
            continue
        }

        // Recursively process nested objects (but not arrays or Dates)
        if (
            typeof value === 'object' &&
            !Array.isArray(value) &&
            value !== null &&
            !(value instanceof Date)
        ) {
            const cleaned = convertEmptyStringsToNull(value as Record<string, unknown>)
            result[key] = cleaned as T[Extract<keyof T, string>]
        } else {
            result[key] = value
        }
    }

    return result
}

/**
 * Used to ensure consistent shape when performing deep equality checks.
 */
const sanitizeArray = <T>(arr: T[]) => {
    return arr.map((item) => {
        if (item instanceof Date) return item
        if (typeof item === 'object' && item !== null) {
            return sanitizePayload(item as Record<string, unknown>)
        }
        return item
    })
}

/**
 * Returns only the changes between two objects.
 *
 * Needed because we only want to send the changed fields to the server to prevent
 * overwriting changed others made.
 */
export function diffObjects<T extends Record<string, unknown>>(
    aInitial: T,
    bInitial: T,
    options?: { skipIdFields?: boolean }
): Partial<T> {
    const a = sanitizePayload(aInitial, options)
    const b = sanitizePayload(bInitial, options)
    const result: Partial<T> = {}
    const keys = new Set([...Object.keys(a), ...Object.keys(b)])

    for (const key of keys) {
        const aValue = a[key]
        const bValue = b[key]

        if (Array.isArray(aValue) && Array.isArray(bValue)) {
            const aSanitized = sanitizeArray(aValue)
            const bSanitized = sanitizeArray(bValue)
            // Only include array if it has actually changed
            if (JSON.stringify(aSanitized) !== JSON.stringify(bSanitized)) {
                result[key as keyof T] = bValue as T[typeof key]
            }
        } else if (
            typeof aValue === 'object' &&
            aValue !== null &&
            typeof bValue === 'object' &&
            bValue !== null &&
            'documentId' in bValue
        ) {
            const diff = diffObjects(
                aValue as Record<string, unknown>,
                bValue as Record<string, unknown>,
                { skipIdFields: true }
            )
            if (Object.keys(diff).length > 0) result[key as keyof T] = diff as T[typeof key]
        } else if (JSON.stringify(aValue) !== JSON.stringify(bValue)) {
            result[key as keyof T] = bValue as T[typeof key]
        }
    }

    return result
}
